import { Marker, Popup } from 'react-leaflet';
import { divIcon } from 'leaflet';
import { useRef, useEffect } from 'react';
import { Location } from '../../types';
import { useMapStore } from '../../store/mapStore';

interface LocationMarkerProps {
  location: Location;
}

const LocationMarker = ({ location }: LocationMarkerProps) => {
  const setSelectedLocation = useMapStore((state) => state.setSelectedLocation);
  const markerRef = useRef<any>(null);

  // Create custom icon based on importance
  const markerSize = location.importance === 'major' ? 40 : 28;
  const markerColor = location.importance === 'major' ? '#C9A227' : '#B87333';

  const customIcon = divIcon({
    html: `
      <div style="
        width: ${markerSize}px;
        height: ${markerSize}px;
        background-color: ${markerColor};
        border: 3px solid #2C1810;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 0 10px rgba(201, 162, 39, 0.6);
        position: relative;
      ">
        <span style="
          color: #F5E6C8;
          font-weight: bold;
          font-size: ${markerSize / 2.5}px;
          transform: rotate(45deg);
          font-family: 'Forum', serif;
        ">${location.id}</span>
        ${
          location.nolansFilmLocation
            ? `<span style="
                position: absolute;
                top: -8px;
                right: -8px;
                background: gold;
                border-radius: 50%;
                width: 16px;
                height: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 10px;
                transform: rotate(45deg);
              ">🎬</span>`
            : ''
        }
      </div>
    `,
    className: 'custom-marker',
    iconSize: [markerSize, markerSize],
    iconAnchor: [markerSize / 2, markerSize],
  });

  // Setup hover events to show/hide popup
  useEffect(() => {
    if (markerRef.current) {
      const marker = markerRef.current;

      marker.on('mouseover', () => {
        marker.openPopup();
      });

      marker.on('mouseout', () => {
        marker.closePopup();
      });

      // Close popup when clicked (before full modal opens)
      marker.on('click', () => {
        marker.closePopup();
      });
    }
  }, []);

  return (
    <Marker
      ref={markerRef}
      position={[location.coordinates.lat, location.coordinates.lng]}
      icon={customIcon}
      eventHandlers={{
        click: () => setSelectedLocation(location),
      }}
    >
      <Popup
        closeButton={false}
        autoClose={false}
        closeOnClick={false}
        className="ancient-popup"
      >
        <div className="bg-parchment-base border-2 border-copper rounded-lg p-4 shadow-lg min-w-[200px]">
          <h3 className="font-display text-xl font-bold text-ink-primary mb-1">
            {location.name}
          </h3>
          <p className="font-ui text-sm text-ink-secondary mb-2">
            {location.modernName}
          </p>
          <p className="font-body text-xs text-copper italic">
            Click to learn more
          </p>
        </div>
      </Popup>
    </Marker>
  );
};

export default LocationMarker;
